import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Input, message } from "antd";
import styled from "styled-components";
import { countryTaxConfigAPI, type CountryTaxConfigRow } from "@/api/countryTaxConfig";
import { CampaignStyledTable } from "@/features/campaign";
import { AppButton, Icon } from "@/shared/components";
import type { CustomTableColumn } from "@/shared/components/Table";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import { businessToday } from "../utils/businessDate";
import { formatLongDate, formatPercent, groupByCountry, type CountryGroup } from "../utils/countryConfig";
import { useColumnVisibility } from "../hooks/useColumnVisibility";
import { toColumnSpecs } from "../utils/columnVisibility";
import { ColumnSettings, ScrollTable } from "./ColumnSettings";
import { AddRateChangeDrawer, RateChangeForm } from "./AddRateChangeDrawer";
import { EndRateModal } from "./EndRateModal";
import { CountryLabel } from "./CountryLabel";

export const countryName = (code: string): string => phoneCountries.find((c) => c.iso === code)?.name ?? code;
const today = businessToday;

/**
 * Admin › Country config (Ticket-348 §5.6). One row per configured country showing the row in force
 * today; click a row for its history in a side panel. Rows are never edited or deleted — a mistake
 * is corrected by adding another row, which ends the previous one.
 */
export const CountryConfigTab = () => {
	const [rows, setRows] = useState<CountryTaxConfigRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [query, setQuery] = useState("");
	const [drawer, setDrawer] = useState<{ open: boolean; countryCode?: string }>({ open: false });
	const [ending, setEnding] = useState<CountryTaxConfigRow | null>(null);
	// The panel tracks a country code rather than a group so a reload after adding or ending a rate
	// refreshes what the open panel shows.
	const [selectedCode, setSelectedCode] = useState<string | null>(null);
	// "add" renders the rate-change form inside the panel instead of a second drawer on top of it.
	const [panelMode, setPanelMode] = useState<"overview" | "add">("overview");
	const openPanel = (code: string) => {
		setSelectedCode(code);
		setPanelMode("overview");
	};

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setRows(await countryTaxConfigAPI.listAll());
		} catch (error) {
			message.error(getApiErrorMessage(error, "Unable to load country configuration"));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const groups = useMemo(() => {
		const all = groupByCountry(rows, today());
		const q = query.trim().toLowerCase();
		return q
			? all.filter(
					(g) =>
						g.countryCode.toLowerCase().includes(q) || countryName(g.countryCode).toLowerCase().includes(q),
				)
			: all;
	}, [query, rows]);
	const selected = useMemo(
		() =>
			selectedCode ? (groupByCountry(rows, today()).find((g) => g.countryCode === selectedCode) ?? null) : null,
		[rows, selectedCode],
	);

	const columns: CustomTableColumn<CountryGroup>[] = [
		{
			title: "Country",
			key: "country",
			width: 200,
			enableSort: true,
			sorter: (a, b) => countryName(a.countryCode).localeCompare(countryName(b.countryCode)),
			render: (_, g) => <CountryLabel code={g.countryCode} />,
		},
		{
			title: "Tax",
			key: "tax",
			width: 200,
			render: (_, g) =>
				g.current ? `${g.current.taxName} · ${g.current.registrationNumberLabel}` : <Muted>Not in force</Muted>,
		},
		{
			title: "Effective rate",
			key: "rate",
			render: (_, g) => (g.current ? formatPercent(g.current.effectiveRate) : "—"),
		},
		{
			title: "Band",
			key: "band",
			width: 110,
			render: (_, g) => (g.current ? `±${g.current.bandPoints} pts` : "—"),
		},
		{
			title: "Route",
			key: "route",
			width: 200,
			render: (_, g) =>
				g.current ? (
					<RouteBadge $partner={g.current.paymentRoute === "local_partner"}>
						{g.current.paymentRoute === "local_partner"
							? `Local partner · ${g.current.partnerName}`
							: "CPI direct"}
					</RouteBadge>
				) : (
					"—"
				),
		},
		{
			title: "Valid from",
			key: "validFrom",
			width: 140,
			render: (_, g) =>
				g.current ? (g.current.validFrom ? formatLongDate(g.current.validFrom) : "Since forever") : "—",
		},
		{
			title: "Next change",
			key: "next",
			width: 200,
			render: (_, g) =>
				g.next ? (
					`→ ${formatPercent(g.next.effectiveRate)} from ${formatLongDate(g.next.validFrom)}`
				) : (
					<Muted>None</Muted>
				),
		},
	];
	const columnSettings = useColumnVisibility("country-config", toColumnSpecs(columns));

	return (
		<>
			<Toolbar>
				<Search
					aria-label="Search countries"
					placeholder="Search countries..."
					suffix={<Icon src="/icon/menu_search.svg" size={22} />}
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<ToolbarRight>
					<AppButton variant="primary" onClick={() => setDrawer({ open: true })}>
						Add rate change
					</AppButton>
					<ColumnSettings
						columns={toColumnSpecs(columns)}
						hidden={columnSettings.hidden}
						onToggle={columnSettings.toggle}
						onReset={columnSettings.reset}
					/>
				</ToolbarRight>
			</Toolbar>
			<Note>
				Rates are date-effective and append-only. Statements already issued keep the rate that applied on their
				issue date; a change here affects future statements only. Click a country for its history and actions.
				Countries not listed are not configured: creators there cannot be sent to Finance until a row exists.
			</Note>
			<ScrollTable $minWidth={columnSettings.minWidth + 48}>
				<CampaignStyledTable<CountryGroup>
					rowKey="countryCode"
					columns={columns.filter((column) => !columnSettings.hidden.includes(String(column.key)))}
					dataSource={groups}
					loading={loading}
					pagination={false}
					locale={{ emptyText: "No countries configured. Add a rate change to configure one." }}
					onRow={(g) => ({ onClick: () => openPanel(g.countryCode), style: { cursor: "pointer" } })}
				/>
			</ScrollTable>
			<Drawer
				open={Boolean(selected)}
				onClose={() => setSelectedCode(null)}
				width={720}
				title={
					selected ? (
						<PanelTitle>
							<CountryLabel code={selected.countryCode} />
							{panelMode === "add" ? <PanelCrumb>· Add rate change</PanelCrumb> : null}
						</PanelTitle>
					) : (
						""
					)
				}
				destroyOnClose
			>
				{selected && panelMode === "add" ? (
					<RateChangeForm
						key={selected.countryCode}
						initialCountryCode={selected.countryCode}
						lockCountry
						rows={rows}
						onCancel={() => setPanelMode("overview")}
						onCreated={() => {
							setPanelMode("overview");
							void load();
						}}
					/>
				) : selected ? (
					<PanelBody>
						<Meta>
							<span>Tax</span>
							<strong>
								{selected.current ? (
									`${selected.current.taxName} · ${selected.current.registrationNumberLabel}`
								) : (
									<Muted>Not in force</Muted>
								)}
							</strong>
							<span>Effective rate</span>
							<strong>{selected.current ? formatPercent(selected.current.effectiveRate) : "—"}</strong>
							<span>Band</span>
							<strong>{selected.current ? `±${selected.current.bandPoints} pts` : "—"}</strong>
							<span>Route</span>
							<strong>
								{selected.current
									? selected.current.paymentRoute === "local_partner"
										? `Local partner · ${selected.current.partnerName}`
										: "CPI direct"
									: "—"}
							</strong>
							<span>Valid from</span>
							<strong>
								{selected.current
									? selected.current.validFrom
										? formatLongDate(selected.current.validFrom)
										: "Since forever"
									: "—"}
							</strong>
							<span>Next change</span>
							<strong>
								{selected.next ? (
									`→ ${formatPercent(selected.next.effectiveRate)} from ${formatLongDate(selected.next.validFrom)}`
								) : (
									<Muted>None</Muted>
								)}
							</strong>
						</Meta>
						<Actions>
							<SmallButton variant="outline" onClick={() => setPanelMode("add")}>
								Add rate change
							</SmallButton>
							{selected.current && selected.current.validTo === null ? (
								<SmallButton variant="ghost" onClick={() => setEnding(selected.current)}>
									End rate
								</SmallButton>
							) : null}
						</Actions>
						<History rows={selected.history} />
					</PanelBody>
				) : null}
			</Drawer>
			<AddRateChangeDrawer
				open={drawer.open}
				initialCountryCode={drawer.countryCode}
				rows={rows}
				onClose={() => setDrawer({ open: false })}
				onCreated={() => {
					setDrawer({ open: false });
					void load();
				}}
			/>
			<EndRateModal
				row={ending}
				onClose={() => setEnding(null)}
				onEnded={() => {
					setEnding(null);
					void load();
				}}
			/>
		</>
	);
};

type RowStatus = "in_force" | "upcoming" | "ended";
const rowStatus = (row: CountryTaxConfigRow, date: string): RowStatus => {
	if (row.validFrom !== null && row.validFrom > date) return "upcoming";
	if (row.validTo !== null && row.validTo <= date) return "ended";
	return "in_force";
};
const STATUS_LABEL: Record<RowStatus, string> = { in_force: "In force", upcoming: "Upcoming", ended: "Ended" };

/** The country's rows, newest first, as double-row cards: period and rates, then band, route, recorded, note. */
const History = ({ rows }: { rows: CountryTaxConfigRow[] }) => {
	const date = today();
	return (
		<Cards aria-label="Rate history">
			{rows.map((row) => {
				const status = rowStatus(row, date);
				return (
					<HistoryCard key={row.id} $status={status}>
						<CardRow>
							<Period>
								{row.validFrom ? formatLongDate(row.validFrom) : "Since forever"}
								<Arrow aria-hidden="true">→</Arrow>
								{row.validTo ? formatLongDate(row.validTo) : "Until further notice"}
							</Period>
							<StatusPill $status={status}>{STATUS_LABEL[status]}</StatusPill>
							<Rates>
								<RateValue>{formatPercent(row.effectiveRate)}</RateValue>
								<RateLabel>effective</RateLabel>
								{row.statutoryRate !== row.effectiveRate ? (
									<>
										<Dot aria-hidden="true">·</Dot>
										<RateValue>{formatPercent(row.statutoryRate)}</RateValue>
										<RateLabel>statutory</RateLabel>
									</>
								) : null}
							</Rates>
						</CardRow>
						<CardRow $secondary>
							<Fact>±{row.bandPoints} pts band</Fact>
							<Dot aria-hidden="true">·</Dot>
							<Fact>
								{row.paymentRoute === "local_partner"
									? `Local partner · ${row.partnerName}`
									: "CPI direct"}
							</Fact>
							<Dot aria-hidden="true">·</Dot>
							<Fact>
								Recorded{" "}
								{new Date(row.createdAt).toLocaleDateString("en-AU", {
									day: "numeric",
									month: "short",
									year: "numeric",
								})}
								{row.createdByName ? ` by ${row.createdByName}` : ""}
							</Fact>
							<NoteText title={row.notes ?? undefined}>{row.notes || "No note"}</NoteText>
						</CardRow>
					</HistoryCard>
				);
			})}
		</Cards>
	);
};

const ToolbarRight = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;
const Toolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 12px;
	flex-wrap: wrap;
`;
const Search = styled(Input)`
	width: 280px;
	&& {
		height: 40px;
		border-color: #cdd4f6;
		border-radius: 8px;
		font-size: 16px;
	}
`;
const Note = styled.p`
	margin: 0 0 16px;
	color: #707070;
	font-size: 13px;
`;
const Muted = styled.span`
	color: #707070;
`;
const RouteBadge = styled.span<{ $partner: boolean }>`
	display: inline-flex;
	padding: 2px 8px;
	border-radius: 24px;
	font-size: 12px;
	font-weight: 500;
	background: ${({ $partner }) => ($partner ? "#ffe3ac" : "#dde2fa")};
	color: ${({ $partner }) => ($partner ? "#bb7f05" : "#2a057f")};
`;
const PanelTitle = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
`;
const PanelCrumb = styled.span`
	color: #707070;
	font-weight: 500;
`;
const PanelBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;
const Meta = styled.dl`
	display: grid;
	grid-template-columns: max-content 1fr;
	gap: 8px 16px;
	margin: 0;
	padding: 12px 16px;
	border: 1px solid #cdd4f6;
	border-radius: 12px;
	font-size: 14px;
	span {
		color: #707070;
	}
	strong {
		color: #303030;
	}
`;
const Actions = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-start;
`;
const SmallButton = styled(AppButton)`
	height: 32px;
	padding: 0 12px;
	font-size: 13px;
`;
const Cards = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;
const HistoryCard = styled.li<{ $status: RowStatus }>`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 12px 16px;
	border-radius: 12px;
	background: ${({ $status }) => ($status === "in_force" ? "#f3f5ff" : "#fff")};
	border: 1px solid ${({ $status }) => ($status === "in_force" ? "#9fadf1" : "#dde2fa")};
	opacity: ${({ $status }) => ($status === "ended" ? 0.8 : 1)};
`;
const CardRow = styled.div<{ $secondary?: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	font-size: ${({ $secondary }) => ($secondary ? "13px" : "14px")};
	color: ${({ $secondary }) => ($secondary ? "#707070" : "#303030")};
`;
const Period = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-weight: 600;
`;
const Arrow = styled.span`
	color: #9b9b9b;
`;
const StatusPill = styled.span<{ $status: RowStatus }>`
	display: inline-flex;
	padding: 1px 8px;
	border-radius: 24px;
	font-size: 12px;
	font-weight: 600;
	background: ${({ $status }) =>
		$status === "in_force" ? "#bfef90" : $status === "upcoming" ? "#ffe3ac" : "#e9e9e9"};
	color: ${({ $status }) => ($status === "in_force" ? "#377608" : $status === "upcoming" ? "#bb7f05" : "#6b6b6b")};
`;
const Rates = styled.span`
	display: inline-flex;
	align-items: baseline;
	gap: 4px;
	margin-left: auto;
`;
const RateValue = styled.strong`
	font-size: 16px;
	color: #303030;
`;
const RateLabel = styled.span`
	font-size: 12px;
	color: #707070;
`;
const Dot = styled.span`
	color: #b0b0b0;
`;
const Fact = styled.span`
	white-space: nowrap;
`;
const NoteText = styled.span`
	flex-basis: 100%;
	color: #565656;
	font-style: italic;
	overflow: hidden;
	text-overflow: ellipsis;
`;
